/**
 * Asistente para imprimir el inventario
 *
 * Recorre por lotes, ordenado por familia, los artículos con existencias que
 * todavía no tienen todas sus etiquetas. Cada lote se imprime en la pantalla de
 * Etiquetas; al confirmar ahí, lo completo sale de esta lista.
 */

import { useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CheckCheck, PartyPopper, Printer, Search } from 'lucide-react'

import api from '@/lib/api'
import { debounce, formatDateTime } from '@/lib/utils'
import { useUrlPage, useUrlState } from '@/hooks/useUrlState'
import { t } from '@/i18n'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { Progress } from '@/components/ui/progress'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toaster'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { clampQuantity, type LabelsLocationState, type Selected } from './ArticleLabelsPage'

interface LabelStatusItem {
  id: number
  code: string
  description: string
  sale_price_1_iva: number
  stock: number
  printed: number
  missing: number
  last_printed_at: string | null
  family_id: number | null
  family_name: string | null
}

interface LabelStatusResponse {
  items: LabelStatusItem[]
  total: number
  page: number
  pages: number
  summary: {
    articles: number
    done: number
    partial: number
    not_printed: number
    stock_units: number
    printed_units: number
  }
  families: { id: number | null; name: string | null; articles: number; pending: number }[]
}

type StatusFilter = 'pending' | 'done' | 'all'

const BATCH_SIZES = [1, 5, 10, 20]

export function ArticleInventoryLabelsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const location = useLocation()
  // En la URL: al volver de imprimir un lote, el asistente sigue en la misma
  // familia y con el mismo tamaño de lote. Cambiar un filtro vuelve a la página 1.
  const [statusParam, setStatus] = useUrlState('status', 'pending')
  const status: StatusFilter = statusParam === 'done' || statusParam === 'all' ? statusParam : 'pending'
  const [family, setFamily] = useUrlState('family', 'all')
  const [batchParam, setBatchParam] = useUrlState('batch', '10')
  const batchSize = BATCH_SIZES.includes(Number(batchParam)) ? Number(batchParam) : 10
  const [page, setPage] = useUrlPage()
  const [search, setSearch] = useUrlState('q')
  const [searchInput, setSearchInput] = useState(search)
  // Lote a marcar, tomado al abrir el diálogo: no cambia si la lista se recarga.
  const [markBatch, setMarkBatch] = useState<LabelStatusItem[] | null>(null)

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value.trim())
      }, 300),
    []
  )

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['label-status', status, family, batchSize, page, search],
    queryFn: async () => {
      const params = new URLSearchParams({ status, page: String(page), page_size: String(batchSize) })
      if (family !== 'all') params.set('family_id', family)
      if (search) params.set('search', search)
      const response = await api.get<LabelStatusResponse>(`/articles/label-status?${params}`)
      return response.data
    },
    placeholderData: (previous) => previous,
  })

  // El lote es lo que falta de cada artículo de esta página. Mientras carga otra
  // página o filtro, lo que se ve es la lista anterior: no se ofrece como lote.
  const batch = isFetching ? [] : (data?.items ?? []).filter((item) => item.missing > 0)
  // Al imprimir, con el mismo tope por artículo que la pantalla de Etiquetas.
  const batchLabels = batch.reduce((sum, item) => sum + clampQuantity(item.missing), 0)

  const toSelected = (item: LabelStatusItem): Selected => ({
    id: item.id,
    code: item.code,
    description: item.description,
    price: item.sale_price_1_iva,
    stock: item.stock,
    quantity: clampQuantity(item.missing),
  })

  const printBatch = () => {
    const state: LabelsLocationState = {
      items: batch.map(toSelected),
      fromInventory: true,
      returnTo: `${location.pathname}${location.search}`,
    }
    navigate('/articles/labels', { state })
  }

  const markLabels = (markBatch ?? []).reduce((sum, item) => sum + item.missing, 0)
  const markPrinted = useMutation({
    mutationFn: (items: LabelStatusItem[]) =>
      api.post('/articles/label-prints', {
        items: items.map((item) => ({ article_id: item.id, quantity: item.missing })),
      }),
    onSuccess: (_, items) => {
      queryClient.invalidateQueries({ queryKey: ['label-status'] })
      toast({ title: t('labels.recorded', { count: items.reduce((sum, item) => sum + item.missing, 0) }) })
      setMarkBatch(null)
      setPage(1)
    },
    onError: () => toast({ title: t('common.errorOccurred'), variant: 'destructive' }),
  })

  const summary = data?.summary
  const familyOptions = [
    { value: 'all', label: t('labels.inventoryAllFamilies') },
    ...(data?.families ?? []).map((f) => ({
      value: String(f.id ?? 0),
      label: t('labels.inventoryFamilyOption', {
        name: f.name ?? t('labels.inventoryNoFamily'),
        count: f.pending,
      }),
    })),
  ]
  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'pending', label: t('labels.inventoryStatusPending') },
    { value: 'done', label: t('labels.inventoryStatusDone') },
    { value: 'all', label: t('labels.inventoryStatusAll') },
  ]
  const allComplete = !!summary && summary.articles > 0 && summary.done === summary.articles

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/articles/labels')}
          aria-label={t('common.back')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('labels.inventoryTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('labels.inventorySubtitle')}</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          {summary ? (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-medium">
                  {t('labels.inventoryArticlesProgress', {
                    done: summary.done.toLocaleString('es-CR'),
                    articles: summary.articles.toLocaleString('es-CR'),
                  })}
                </p>
                <p className="text-sm text-muted-foreground tabular-nums">
                  {summary.articles ? Math.floor((summary.done / summary.articles) * 100) : 0} %
                </p>
              </div>
              <Progress value={summary.done} max={Math.max(summary.articles, 1)} className="h-3" />
              {summary.partial > 0 && (
                <p className="text-sm text-muted-foreground">
                  {t(summary.partial === 1 ? 'labels.inventoryPartialNoteOne' : 'labels.inventoryPartialNote', {
                    count: summary.partial.toLocaleString('es-CR'),
                  })}
                </p>
              )}
            </>
          ) : (
            <Skeleton className="h-16 w-full" />
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
        <div className="flex gap-1" role="radiogroup" aria-label={t('labels.inventoryStatusAll')}>
          {statusOptions.map((option) => (
            <Button
              key={option.value}
              size="sm"
              variant={status === option.value ? 'default' : 'outline'}
              role="radio"
              aria-checked={status === option.value}
              onClick={() => {
                setStatus(option.value)
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <SearchableSelect
          options={familyOptions}
          value={family}
          onValueChange={(value) => {
            setFamily(value)
          }}
          placeholder={t('labels.inventoryFamily')}
          searchPlaceholder={t('common.search')}
          className="w-full md:w-[260px] h-9 text-sm"
        />
        <div className="relative w-full md:w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
              debouncedSearch(e.target.value)
            }}
            placeholder={t('common.search')}
            className="pl-9 h-9"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground md:ml-auto">
          {t('labels.inventoryBatchSize')}
          <select
            value={batchSize}
            onChange={(e) => {
              setBatchParam(e.target.value)
            }}
            className="h-9 rounded-md border border-input bg-background px-2 text-foreground"
          >
            {BATCH_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {batch.length > 0 && (
        <Card className="border-primary">
          <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <p className="font-medium">
                {t('labels.inventoryBatch', { articles: batch.length, labels: batchLabels.toLocaleString('es-CR') })}
              </p>
              {batch.some((item) => item.missing > clampQuantity(item.missing)) && (
                <p className="text-xs text-muted-foreground">{t('labels.inventoryBatchCapped')}</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setMarkBatch(batch)}>
                <CheckCheck className="h-4 w-4 mr-2" />
                {t('labels.inventoryMarkPrinted')}
              </Button>
              <Button onClick={printBatch}>
                <Printer className="h-4 w-4 mr-2" />
                {t('labels.inventoryPrintBatch')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : allComplete && status === 'pending' ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <PartyPopper className="h-10 w-10 text-primary" />
          <p className="font-medium">{t('labels.inventoryAllComplete')}</p>
        </div>
      ) : !data?.items.length ? (
        <p className="py-12 text-center text-sm text-muted-foreground">{t('labels.inventoryEmpty')}</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y">
              {data.items.map((item, index) => {
                const newFamily = index === 0 || data.items[index - 1].family_id !== item.family_id
                const done = item.missing === 0
                return (
                  <li key={item.id}>
                    {newFamily && (
                      <p className="bg-muted/50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {item.family_name ?? t('labels.inventoryNoFamily')}
                      </p>
                    )}
                    <div className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 px-4 py-2.5 sm:grid-cols-[1fr_repeat(3,5rem)_9rem]">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{item.description}</p>
                        <p className="text-xs text-muted-foreground">{item.code}</p>
                      </div>
                      <div className="text-right sm:hidden">
                        <Badge variant={done ? 'default' : item.printed > 0 ? 'warning' : 'secondary'}>
                          {done ? t('labels.inventoryDone') : `${t('labels.inventoryMissing')} ${item.missing}`}
                        </Badge>
                      </div>
                      <Stat label={t('labels.inventoryStock')} value={item.stock} />
                      <Stat label={t('labels.inventoryPrinted')} value={item.printed} />
                      <Stat label={t('labels.inventoryMissing')} value={item.missing} strong={!done} />
                      <div className="hidden sm:block text-right text-xs text-muted-foreground">
                        <span className="block">{t('labels.inventoryLastPrinted')}</span>
                        {item.last_printed_at ? formatDateTime(item.last_printed_at) : t('labels.inventoryNever')}
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          </CardContent>
        </Card>
      )}

      {data && data.pages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            {t('common.page')} {data.page} {t('common.of')} {data.pages}
          </p>
          <Pagination currentPage={page} totalPages={data.pages} onPageChange={setPage} />
        </div>
      )}

      <AlertDialog
        open={markBatch !== null}
        onOpenChange={(open) => {
          if (!open && !markPrinted.isPending) setMarkBatch(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('labels.inventoryMarkTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('labels.inventoryMarkDescription', { labels: markLabels.toLocaleString('es-CR'), articles: markBatch?.length ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={markPrinted.isPending}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                // El diálogo se cierra al terminar: así no se puede mandar dos veces.
                event.preventDefault()
                if (markBatch) markPrinted.mutate(markBatch)
              }}
              disabled={markPrinted.isPending}
            >
              {t('labels.inventoryMarkPrinted')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function Stat({ label, value, strong = false }: { label: string; value: number; strong?: boolean }) {
  return (
    <div className="hidden sm:block text-right">
      <span className="block text-xs text-muted-foreground">{label}</span>
      <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`}>{value}</span>
    </div>
  )
}
